import { VAT_RATE } from "../types/money";
import { parseVat, VIES_COUNTRIES } from "../business/validation";

/**
 * Btw verleggen bij een intracommunautaire levering (ICL).
 *
 * Verkoop je aan een ondernemer in een andere EU-lidstaat en verlaten de goederen Nederland, dan
 * is het tarief 0% en wordt de btw naar de afnemer verlegd. Die betaalt hem in zijn eigen land aan.
 * Op de factuur hoort dan "Btw verlegd" te staan met beide btw-nummers.
 *
 * **Waarom dit één functie is.** De verleiding is om dit op de plek te regelen waar het opvalt —
 * in de checkout, of op de factuur. Dan gaan browser en server verschillende dingen zeggen en
 * rekent de klant iets anders af dan er op zijn factuur komt. Alles wat over btw beslist, beslist
 * het hier; de server roept dezelfde functie aan als het scherm.
 *
 * **Drie voorwaarden, en alle drie zijn nodig:**
 *  1. de goederen gaan naar een andere EU-lidstaat dan Nederland;
 *  2. de afnemer heeft een geldig btw-nummer uit een andere lidstaat (geverifieerd bij VIES);
 *  3. de goederen verlaten Nederland ook daadwerkelijk.
 *
 * Die derde is de valkuil: haalt een Belgisch bedrijf zélf op in Oosterhout, dan blijven de
 * goederen in Nederland en geldt gewoon 21%. Dat is precies het soort uitzondering waar een
 * belastingcontrole op valt, dus staat hij hier expliciet.
 */

export type BtwStatus =
  | "verlegd"
  | "nl-tarief"
  | "geen-nummer"
  | "nummer-ongeldig"
  /**
   * VIES was niet bereikbaar, dus we kónden het nummer niet controleren. Dat is iets ánders dan
   * een ongeldig nummer, al is het tarief hetzelfde (NL, want 0% claimen zonder bewijs mag niet).
   * Het verschil staat hier apart omdat het de klant iets verwíjt wat hij niet gedaan heeft — en
   * omdat juist deze orders nagelopen moeten worden zodra VIES weer antwoordt. Bij order 1091
   * (NV Barco) stond "nummer-ongeldig" terwijl het nummer gewoon klopte; de klant betaalde
   * € 151,14 btw die hij niet hoefde te betalen. (19-10-2026.)
   */
  | "niet-gecontroleerd"
  | "afhalen"
  | "buiten-eu";

export interface BtwSituatie {
  /** Land waar bezorgd wordt (ISO-2). Leeg = onbekend → veilig terugvallen op NL-tarief. */
  bezorgland?: string | null;
  /** Btw-nummer zoals de klant het invulde. */
  vatNumber?: string | null;
  /** Uitslag van de VIES-controle. `null` = niet gecontroleerd. */
  viesGeldig?: boolean | null;
  /** De klant haalt zelf op → goederen verlaten Nederland niet. */
  afhalen?: boolean;
}

export interface BtwUitkomst {
  /** 0 bij verlegging, anders het Nederlandse tarief. */
  tarief: number;
  verlegd: boolean;
  status: BtwStatus;
  /** Leesbare reden — gaat mee naar de admin en verklaart waarom het zo is gelopen. */
  reden: string;
}

const NL_TARIEF = (status: BtwStatus, reden: string): BtwUitkomst => ({
  tarief: VAT_RATE,
  verlegd: false,
  status,
  reden,
});

/**
 * Bepaalt of de btw verlegd wordt. Bewust conservatief: bij twijfel het Nederlandse tarief, want
 * te veel btw rekenen is een correctie waard en te weinig een naheffing.
 */
export function bepaalBtw(s: BtwSituatie): BtwUitkomst {
  if (s.afhalen) {
    return NL_TARIEF("afhalen", "Afhalen in Oosterhout: de goederen verlaten Nederland niet, dus Nederlandse btw.");
  }
  const land = (s.bezorgland ?? "").toUpperCase();
  if (!land || land === "NL") {
    return NL_TARIEF("nl-tarief", "Bezorging binnen Nederland.");
  }
  if (!VIES_COUNTRIES.has(land)) {
    // Export buiten de EU kent een eigen 0%-regeling mét uitvoerbewijs; die kunnen we hier niet
    // aantonen, dus rekenen we gewoon btw in plaats van een tarief te claimen dat we niet kunnen
    // onderbouwen.
    return NL_TARIEF("buiten-eu", `Bezorging naar ${land}: buiten de EU, geen intracommunautaire levering.`);
  }
  const vat = (s.vatNumber ?? "").trim();
  if (!vat) {
    return NL_TARIEF("geen-nummer", `Bezorging naar ${land} zonder btw-nummer: particulier tarief.`);
  }
  const p = parseVat(vat);
  if (!p || !VIES_COUNTRIES.has(p.country) || p.country === "NL") {
    return NL_TARIEF("nummer-ongeldig", `Btw-nummer ${vat} is geen geldig EU-nummer buiten Nederland.`);
  }
  if (s.viesGeldig === false) {
    return NL_TARIEF("nummer-ongeldig", `Btw-nummer ${vat} is volgens VIES niet geldig.`);
  }
  if (s.viesGeldig !== true) {
    // Niet kunnen controleren ≠ afgekeurd. Zelfde tarief (0% claimen zonder bewijs mag niet),
    // maar een eigen status zodat deze orders terug te vinden zijn als VIES weer werkt.
    return NL_TARIEF(
      "niet-gecontroleerd",
      `Btw-nummer ${vat} kon niet bij VIES gecontroleerd worden (VIES was onbereikbaar). ` +
        `Daarom is het Nederlandse tarief gerekend; controleer het nummer zodra VIES weer antwoordt.`,
    );
  }
  return {
    tarief: 0,
    verlegd: true,
    status: "verlegd",
    reden: `Intracommunautaire levering naar ${land}, btw verlegd naar ${vat}.`,
  };
}

/** Vaste zin op de factuur waar anders het btw-bedrag staat. */
export const BTW_VERLEGD_LABEL = "Btw verlegd";

/**
 * Wettelijke verwijzing die op een ICL-factuur hoort. Zonder deze vermelding is de factuur
 * formeel niet in orde, ook al klopt het bedrag.
 */
export const BTW_VERLEGD_TOELICHTING =
  "Intracommunautaire levering — btw verlegd naar de afnemer (art. 138 Btw-richtlijn 2006/112/EG).";
